import React, { memo, useCallback, useRef, useState } from 'react';
import { type ScrollViewInstance, StyleSheet, View } from 'react-native';

import { startCase } from 'lodash';

import { type AllergenProperty, IngredientKind, ModifierGroupVariation } from '@sg/graphql-schema';
import {
  type CustomizationMachineTypes,
  CustomizationReview,
} from '@sg/shared-fe-features-customization';
import { useDietaryRestrictions } from '@sg/shared-fe-features-menu';

import { CustomizationSelectedIngredients } from '@order/features-customization';

import { useDietaryPreferencesMachine } from '../../../../../../../../context';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationIngredientsReviewPanel = memo((props: Props) => {
  const {
    product,
    activeModifications,
    modifierGroups,
    modificationLimits,
    addIngredientModification,
    removeIngredientModification,
    completelyRemoveIngredientModification,
    getFormattedQuantity,
    onAddMoreIngredients,
    checkIfCanAddMore,
  } = props;

  const { restrictions } = useDietaryPreferencesMachine({
    referrer: 'Customization',
  });
  const getRestrictionNotice = useDietaryRestrictions({ restrictions });

  // ─── Refs ────────────────────────────────────────────────────────────

  const detailsContainerRef = useRef<ScrollViewInstance>(null);

  // ─── State ───────────────────────────────────────────────────────────

  const [activeIngredientId, setActiveIngredientId] = useState<string>();

  const clearActiveIngredientId = useCallback(() => {
    setActiveIngredientId(undefined);
  }, []);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.reviewPanelContainer}>
      <CustomizationSelectedIngredients.Container>
        <CustomizationSelectedIngredients.List>
          {modifierGroups?.map((group, index) => {
            const isLastSection = index === product.modifierGroups.length - 1;

            const ingredientsForThisSection = activeModifications.filter(
              (modification) => modification.modificationGroupId === group.id,
            );

            return (
              <CustomizationSelectedIngredients.IngredientKindSection
                key={group.id}
                ingredientKindName={group.displayName}
                helperText={group.helperText}
                style={isLastSection ? styles.withoutBorder : null}
                formattedQuantity={getFormattedQuantity(group.name)}
              >
                {ingredientsForThisSection.map((modification) => {
                  const isActive = activeIngredientId === modification.ingredient?.id;

                  const isDressing = modification.ingredient?.kind === IngredientKind.Dressing;

                  const maxPortionsForGroup = modificationLimits[group.name]?.maxAggregateQuantity ?? Number.POSITIVE_INFINITY; // prettier-ignore
                  const isSplitPortion = modification.modificationGroupVariation === ModifierGroupVariation.SplitPortion; // prettier-ignore
                  const isHalfPortion = isSplitPortion && modification.quantity < maxPortionsForGroup; // prettier-ignore

                  const allergenProperties = modification?.ingredient?.allergenProperties ?? [];
                  const restrictionNotice = getRestrictionNotice({ allergenProperties });

                  const dietaryRestrictions = allergenProperties
                    .map(prepareDietaryRestrictions)
                    .join(', ');

                  if (!modification.ingredient) {
                    return null;
                  }

                  return (
                    <CustomizationSelectedIngredients.ListItem
                      key={modification.ingredient.id}
                      isActive={isActive}
                      shouldScrollToSelf={false}
                      scrollViewRef={detailsContainerRef}
                    >
                      <CustomizationSelectedIngredients.IngredientV2
                        ingredientId={modification.ingredient.id}
                        ingredientName={modification.ingredient.name}
                        ingredientAsset={modification.ingredient.asset}
                        quantity={modification.quantity}
                        isActive={isActive}
                        isDisabled={isDressing}
                        restrictionNotice={restrictionNotice}
                        dietaryRestrictions={dietaryRestrictions}
                        isSplitPortion={isSplitPortion}
                        isHalfPortion={isHalfPortion}
                        setActive={setActiveIngredientId}
                        setInactive={clearActiveIngredientId}
                        onIncreaseQuantity={addIngredientModification}
                        onDecreaseQuantity={removeIngredientModification}
                        onRemove={completelyRemoveIngredientModification}
                      />
                    </CustomizationSelectedIngredients.ListItem>
                  );
                })}

                <CustomizationReview.AddMoreIngredientsButton
                  modificationGroupId={group.id}
                  modificationGroupName={group.displayName}
                  onPress={onAddMoreIngredients}
                  isDisabled={!checkIfCanAddMore(group.name)}
                />
              </CustomizationSelectedIngredients.IngredientKindSection>
            );
          })}
        </CustomizationSelectedIngredients.List>
      </CustomizationSelectedIngredients.Container>
    </View>
  );
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

function prepareDietaryRestrictions(allergenProperty: AllergenProperty) {
  return startCase(allergenProperty.name.toLowerCase().replace('_', ' '));
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  reviewPanelContainer: {
    flex: 1,
  },
  withoutBorder: {
    borderBottomWidth: 0,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  product: CustomizationMachineTypes.Product;
  activeModifications: CustomizationMachineTypes.Modification[];
  modifierGroups: CustomizationMachineTypes.ExtendedModifierGroup[] | undefined;
  modificationLimits: CustomizationMachineTypes.ModificationGroupLimits;
  addIngredientModification: (ingredientId: string) => void;
  removeIngredientModification: (ingredientId: string) => void;
  completelyRemoveIngredientModification: (ingredientId: string) => void;
  getFormattedQuantity: (groupName: string) => string | undefined;
  onAddMoreIngredients: (groupId: string, groupName: string) => void;
  checkIfCanAddMore: (groupId: string) => boolean;
};
